<template>
    <div style="text-align: center; padding: 40px 0;">
        <h3 style="margin-bottom: 20px;">
            {{ title }}
        </h3>

        <!-- Process Progress -->
        <div style="max-width: 500px; margin: 0 auto;">
            <div style="margin-bottom: 8px; font-weight: 500;">
                {{ statusMessage || defaultStatusMessage }}
            </div>
            <v-progress-linear :value="progressPercentage" :indeterminate="!progressPercentage" color="primary"
                height="12" rounded>
            </v-progress-linear>
            <div style="margin-top: 8px; font-size: 14px; color: #666;">
                {{ progressText }}
            </div>
        </div>

        <p style="margin-top: 20px; color: #666;">
            {{ description }}
        </p>
    </div>
</template>

<script>
export default {
    name: 'import-process-progress',

    props: {
        title: {
            type: String,
            required: true
        },
        description: {
            type: String,
            required: true
        },
        processId: {
            type: String,
            required: false
        },
        importType: {
            type: String,
            required: true
        },
        organizationId: {
            type: String,
            required: true
        },
        defaultStatusMessage: {
            type: String,
            default: 'Processing...'
        },
        pollingInterval: {
            type: Number,
            default: 2000
        },
        pollingTimeout: {
            type: Number,
            default: 300000 // 5 minutes
        }
    },

    data() {
        return {
            statusMessage: '',
            progressPercentage: null,
            progressText: '',
            pollingIntervalId: null,
            pollingStartTime: null
        };
    },

    mounted() {
        this.startPolling();
    },

    beforeDestroy() {
        this.stopPolling();
    },

    methods: {
        startPolling() {
            if (this.pollingIntervalId) {
                clearInterval(this.pollingIntervalId);
            }

            this.pollingStartTime = Date.now();
            this.pollingIntervalId = setInterval(() => {
                // Check for timeout
                if (Date.now() - this.pollingStartTime > this.pollingTimeout) {
                    this.stopPolling();
                    this.$emit('failed', { exception: 'Process timed out. Please try again.' });
                    return;
                }

                this.checkProgress();
            }, this.pollingInterval);
        },

        checkProgress() {
            if (!this.processId) return;

            this.$rc.rest({
                'spinner': false,
                'endpoint': 'GET: /api/v1/process/' + this.processId,
                'params': {},

                'on_success': (pte) => {
                    this.updateProgress(pte);
                },

                'on_error': () => {
                    // Log the error but continue polling - temporary network issues shouldn't fail the whole process
                    // Continue polling on error unless we've been polling for too long
                }
            });
        },

        updateProgress(pte) {
            
            if (pte.state === 'finished') {
                // Simulate completion to 100% if not already there
                if (this.progressPercentage !== 100) {
                    this.progressPercentage = 100;
                    this.statusMessage = 'Completing...';
                    this.progressText = '100% complete';
                }
                this.onComplete(pte);
            } else if (pte.state === 'failed') {
                this.onFailed(pte);
            } else if (pte.state === 'running') {
                // Use backend progress_message if available, otherwise fall back to default
                if (pte.status.progress_message) {
                    this.statusMessage = pte.progress_message;
                } else {
                    this.statusMessage = this.getRunningMessage();
                }

                // Update progress text based on percentage or default
                if (pte.status.progress_percent !== undefined) {
                    this.progressText = `${pte.status.progress_percent}% complete`;
                } else {
                    this.progressText = 'Processing...';
                }
            } else {
                // Use backend progress_message if available, otherwise fall back to default
                if (pte.progress_message) {
                    this.statusMessage = pte.progress_message;
                } else {
                    this.statusMessage = this.defaultStatusMessage;
                }

                if (pte.progress_percent !== undefined) {
                    this.progressText = `${pte.progress_percent}% complete`;
                } else {
                    this.progressText = 'Processing...';
                }
            }

            // Update progress percentage if available from backend
            if (pte.progress_percent !== undefined) {
                this.progressPercentage = pte.progress_percent;
            }

            // Emit progress update
            this.$emit('progress', pte);
        },

        getRunningMessage() {
            switch (this.importType) {
                case 'consent':
                    return 'Processing consent records...';
                case 'recipient':
                    return 'Processing recipient records...';
                case 'contact':
                    return 'Processing contact records...';
                default:
                    return 'Processing records...';
            }
        },

        onComplete(pte) {
            this.stopPolling();
            this.$emit('complete', pte);
        },

        onFailed(pte) {
            this.stopPolling();
            this.$emit('failed', pte);
        },

        stopPolling() {
            if (this.pollingIntervalId) {
                clearInterval(this.pollingIntervalId);
                this.pollingIntervalId = null;
            }
        }
    }
};
</script>
